import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Film, Image as ImageIcon, Code2, Mic, Megaphone, LayoutGrid, GripVertical } from "lucide-react";
import api from "@/lib/api";

const STAGE_META = {
  backlog: { label: "Backlog", color: "border-slate-500/40", dot: "bg-slate-400" },
  in_progress: { label: "In Progress", color: "border-cyan-500/40", dot: "bg-cyan-400" },
  review: { label: "Review", color: "border-amber-500/40", dot: "bg-amber-400" },
  published: { label: "Published", color: "border-emerald-500/40", dot: "bg-emerald-400" },
  blocked: { label: "Blocked", color: "border-red-500/40", dot: "bg-red-400" },
};
const KIND_ICON = { video: Film, image: ImageIcon, app: Code2, voice: Mic, dub: Mic, ad: Megaphone };
const ago = (iso) => { const m = Math.max(1, Math.round((Date.now() - new Date(iso)) / 60000)); return m < 60 ? `${m}m` : m < 1440 ? `${Math.round(m / 60)}h` : `${Math.round(m / 1440)}d`; };

function Card({ c, onDragStart }) {
  const Icon = KIND_ICON[c.kind] || LayoutGrid;
  return (
    <div draggable onDragStart={(e) => onDragStart(e, c.id)} data-testid={`kanban-card-${c.id}`}
      className="rounded-xl border border-white/10 bg-[#0E1019] p-3 cursor-grab active:cursor-grabbing hover:border-purple-500/40 transition-colors">
      <div className="flex items-start gap-2">
        <GripVertical size={13} className="text-slate-600 mt-0.5 shrink-0" />
        <Icon size={14} className="text-cyan-400 mt-0.5 shrink-0" />
        <div className="text-xs font-semibold leading-snug line-clamp-2 flex-1">{c.title}</div>
      </div>
      <div className="mt-2 flex flex-wrap gap-1">
        {c.features.slice(0, 4).map((f) => <span key={f} className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-white/5 text-slate-400">{f}</span>)}
      </div>
      <div className="mt-2 flex items-center justify-between text-[10px] font-mono text-slate-500">
        <span className="truncate max-w-[60%]">{c.owner?.name || c.owner?.email}</span>
        <span>{c.media_count ? `${c.media_count} asset · ` : ""}{ago(c.created_at)}</span>
      </div>
    </div>
  );
}

export function KanbanBoard() {
  const [board, setBoard] = useState(null);
  const [kind, setKind] = useState("all");
  const load = useCallback(() => api.get("/admin/board").then((r) => setBoard(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const onDragStart = (e, id) => e.dataTransfer.setData("text/plain", id);
  const onDrop = async (e, stage) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain");
    const card = board.cards.find((c) => c.id === id);
    if (!card || card.stage === stage) return;
    setBoard({ ...board, cards: board.cards.map((c) => (c.id === id ? { ...c, stage } : c)) });
    try { await api.put(`/admin/board/${id}/stage`, { stage }); toast.success(`Moved to ${STAGE_META[stage].label}`); }
    catch (err) { toast.error(err?.response?.data?.detail || "Move failed"); load(); }
  };

  if (!board) return <div className="glass rounded-2xl p-5 text-xs font-mono text-slate-500 mb-6 animate-pulse">Loading project board…</div>;
  const kinds = ["all", ...Array.from(new Set(board.cards.map((c) => c.kind)))];
  const visible = board.cards.filter((c) => kind === "all" || c.kind === kind);

  return (
    <div data-testid="kanban-board" className="glass rounded-2xl p-4 mb-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="font-display font-semibold text-sm flex items-center gap-2"><LayoutGrid size={15} className="text-purple-400" /> Project Board · all users</div>
          <div className="text-[10px] font-mono text-slate-500 mt-0.5">Drag cards between stages · features per project shown as chips</div>
        </div>
        <div className="flex gap-1">
          {kinds.map((k) => <button key={k} data-testid={`kanban-filter-${k}`} onClick={() => setKind(k)} className={`text-[10px] font-mono px-2 py-1 rounded-md border capitalize ${kind === k ? "border-purple-500/40 bg-purple-500/15 text-purple-200" : "border-white/10 text-slate-500 hover:text-white"}`}>{k}</button>)}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        {board.stages.map((s) => {
          const cards = visible.filter((c) => c.stage === s);
          return (
            <div key={s} data-testid={`kanban-col-${s}`} onDragOver={(e) => e.preventDefault()} onDrop={(e) => onDrop(e, s)}
              className={`rounded-2xl border-t-2 ${STAGE_META[s].color} bg-white/[0.02] p-2 min-h-[220px]`}>
              <div className="flex items-center justify-between px-1 mb-2">
                <span className="text-[10px] font-mono uppercase tracking-widest text-slate-400 flex items-center gap-1.5"><span className={`w-1.5 h-1.5 rounded-full ${STAGE_META[s].dot}`} /> {STAGE_META[s].label}</span>
                <span data-testid={`kanban-count-${s}`} className="text-[10px] font-mono text-slate-500">{cards.length}</span>
              </div>
              <div className="space-y-2">{cards.map((c) => <Card key={c.id} c={c} onDragStart={onDragStart} />)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
